// The product flow (Mitch: "a very clear, visual way to see the lifecycle of a product from base components to final
// product"): a product's BOM as a tree with the finished product at the bottom and every part above what it goes into,
// each box saying who makes or supplies it, the same way everywhere: made in-house at a place ("Elecrow makes"), ordered
// from elsewhere by the CM ("Elecrow orders"), or bought by HHL and sent to the CM ("HHL sends"; "HHL buys" for what HHL
// builds itself). Read from resolve() like every other screen: a sub-assembly built inside the build (a phantom, or one
// on a "CM makes" line) is a box with its parts above it; one with a build of its own (stocked, its BOM's Made by) is a
// box made at its own maker, its parts above it too. Services aren't boxes: they're the maker's work.
// Pure: web/src/pages/flow/ProductFlowPage.tsx draws it; layoutFlow() places the boxes.
import { type BomIndex, type BomItem, type BomRow, bomOf, resolve, variantOptions } from "./boms.ts";

/** Who makes or supplies a box's part: made in-house at `place`, ordered by `place`, sent by HHL, bought by HHL. */
export type FlowWho = "made" | "ordered" | "sent" | "bought";

export type FlowNode = {
  /** Unique in the tree (the lines' path). */
  key: string;
  item: BomItem;
  /** Per finished unit of the product at the bottom (scrap included). */
  qty: number;
  who: FlowWho;
  /** Where it's made, or who orders it ("Elecrow", "HHL"). */
  place: string;
  /** Who it's bought from (the preferred vendor), when someone buys it; null when nobody is linked. */
  from: string | null;
  /** A build of its own (a stocked sub-assembly's own make PO), not made inside what uses it. */
  ownBuild: boolean;
  children: FlowNode[];
};

/** Names for the boxes: a vendor's short name, and an item's preferred vendor's. */
export type FlowNames = { vendor: (id: string) => string; supplier: (itemId: string) => string | null };

/** The tag a box shows: "Elecrow makes", "Elecrow orders", "HHL sends", "HHL buys". */
export const whoText = (n: Pick<FlowNode, "who" | "place">) =>
  n.who === "made" ? `${n.place} makes` : n.who === "ordered" ? `${n.place} orders` : n.who === "sent" ? "HHL sends" : "HHL buys";

/** The place a BOM is built at: its Made by's short name, else HHL. */
const makerOf = (names: FlowNames, b: BomRow) => (b.make_vendor_id ? names.vendor(b.make_vendor_id) : "HHL");

/**
 * The flow of `item` (a product's variant, or a sub-assembly) built from `bom`: the item as the root, made at its BOM's
 * maker, with its parts as children, each child's own parts above it.
 */
export function productFlow(ix: BomIndex, names: FlowNames, bom: BomRow, item: BomItem): FlowNode {
  const options = bom.shopify_product_id ? variantOptions(item) : {};
  const build = (b: BomRow, it: BomItem, qty: number, key: string, stack: Set<string>, own: boolean): FlowNode => {
    const place = makerOf(names, b);
    const node: FlowNode = { key, item: it, qty, who: "made", place, from: null, ownBuild: own, children: [] };
    const r = resolve(ix, b, b.shopify_product_id ? it : undefined, { options });
    // Phantom headings (built here) take the lines under them, by their path.
    const parents = new Map<string, FlowNode>([["", node]]);
    for (const l of r.lines) {
      if (l.item.kind === "service" || l.qty <= 0) continue;
      const parent = parents.get(l.via.join("/")) ?? node;
      const at = `${key}/${l.key}`;
      const per = l.qty * qty;
      const cm = place !== "HHL";
      if (l.phantom) {
        const child: FlowNode = {
          key: at,
          item: l.item,
          qty: per,
          who: l.supply === "cm_buys" ? "ordered" : "made",
          place,
          from: null,
          ownBuild: false,
          children: [],
        };
        parent.children.push(child);
        parents.set([...l.via, l.itemId].join("/"), child);
        continue;
      }
      const sub = l.stocked && l.item.source === "local" ? bomOf(ix, l.item) : undefined;
      if (sub && l.supply !== "cm_buys" && !stack.has(l.itemId)) {
        parent.children.push(build(sub, l.item, per, at, new Set(stack).add(l.itemId), true));
        continue;
      }
      const who: FlowWho = !cm ? "bought" : l.supply === "hhl_consigns" ? "sent" : l.supply === "cm_makes" ? "made" : "ordered";
      parent.children.push({
        key: at,
        item: l.item,
        qty: per,
        who,
        place: who === "sent" || who === "bought" ? "HHL" : place,
        from: who === "made" ? null : names.supplier(l.itemId),
        ownBuild: false,
        children: [],
      });
    }
    return node;
  };
  return build(bom, item, 1, item.id, new Set([item.id]), false);
}

/**
 * A build's state on its boxes: each box's line of the build's readiness (the Make plan's, by part) and the place it's
 * built at, and inside a sub-assembly with a build of its own, that sub-assembly's next build's (`own(itemId)`) at its
 * own maker; keyed by box. The boxes the maker makes or orders have none: they're the maker's to do.
 */
export function flowLines<L extends { itemId: string }>(
  root: FlowNode,
  lines: readonly L[],
  own: (itemId: string) => readonly L[] | undefined,
): Map<string, { line: L; at: string }> {
  const out = new Map<string, { line: L; at: string }>();
  const walk = (n: FlowNode, by: Map<string, L>, at: string) => {
    for (const c of n.children) {
      const line = by.get(c.item.id);
      if (line) out.set(c.key, { line, at });
      if (c.ownBuild) walk(c, new Map((own(c.item.id) ?? []).map((x) => [x.itemId, x])), c.place);
      else walk(c, by, at);
    }
  };
  walk(root, new Map(lines.map((l) => [l.itemId, l])), root.place);
  return out;
}

export type FlowSize = {
  w: number;
  h: number;
  gapX: number;
  gapY: number;
  /** Inside a block of parts: between its rows, around its boxes, and its heading. */
  gapIn: number;
  pad: number;
  label: number;
};
export const FLOW_SIZE: FlowSize = { w: 208, h: 96, gapX: 20, gapY: 56, gapIn: 14, pad: 12, label: 22 };
/** A box with this many parts that have no parts of their own packs them into one block (GC Ultimate 2's 30). */
export const PACK_MIN = 5;
/** In a block, made in-house first, then ordered, then what HHL sends or buys, each in BOM order. */
const WHO_ORDER: FlowWho[] = ["made", "ordered", "sent", "bought"];

export type PlacedNode = { node: FlowNode; x: number; y: number };
/** A block of parts packed above what they go into: its box (x, y its top left, w, h) and how many. */
export type PlacedBlock = { key: string; x: number; y: number; w: number; h: number; count: number };
/** A line from a part (or a block's) bottom middle at `x1` down to the top middle `x2` of what it goes into, whose top is `y`. */
export type FlowEdge = { key: string; x1: number; x2: number; y: number };
export type FlowLayout = { root: PlacedNode; nodes: PlacedNode[]; blocks: PlacedBlock[]; edges: FlowEdge[]; width: number; height: number };

/**
 * Where each box goes: the root at the bottom, each part a row above what it goes into, the parts left to right in BOM
 * order and each box centred over its own parts. A box's parts with none of their own, PACK_MIN or more of them, pack
 * into one block (about as tall as wide) after its other parts, with one line to it, so a product with 30
 * loose parts is a block rather than a row 30 boxes long.
 */
export function layoutFlow(root: FlowNode, s: FlowSize = FLOW_SIZE): FlowLayout {
  const nodes: PlacedNode[] = [];
  const blocks: PlacedBlock[] = [];
  const links: { key: string; x1: number; to: PlacedNode }[] = [];
  let next = 0;
  // Heights are measured up from the root's top while placing (a box's y is its top's), and turned into page y at the end.
  const pack = (n: FlowNode, parts: FlowNode[], bottom: number): PlacedBlock => {
    const sorted = [...parts].sort((a, b) => WHO_ORDER.indexOf(a.who) - WHO_ORDER.indexOf(b.who));
    // Taller than wide (a box is twice as wide as tall), since the rest of a tree spreads sideways.
    const rows = Math.ceil(sorted.length / Math.ceil(Math.sqrt(sorted.length * 0.6)));
    const cols = Math.ceil(sorted.length / rows);
    const w = 2 * s.pad + cols * s.w + (cols - 1) * s.gapX;
    const h = 2 * s.pad + s.label + rows * s.h + (rows - 1) * s.gapIn;
    const block = { key: `${n.key}/parts`, x: next, y: bottom + h, w, h, count: parts.length };
    sorted.forEach((c, i) => {
      const row = Math.floor(i / cols); // from the top
      nodes.push({ node: c, x: next + s.pad + (i % cols) * (s.w + s.gapX), y: block.y - s.pad - s.label - row * (s.h + s.gapIn) });
    });
    blocks.push(block);
    next += w + s.gapX;
    return block;
  };
  const place = (n: FlowNode, up: number): PlacedNode => {
    const loose = n.children.filter((c) => !c.children.length);
    const packed = new Set(loose.length >= PACK_MIN ? loose : []);
    // What goes into n, a row up: each box's (or the block's) key and middle.
    const parts: { key: string; mid: number }[] = [];
    for (const c of n.children) if (!packed.has(c)) parts.push({ key: c.key, mid: place(c, up + s.gapY + s.h).x + s.w / 2 });
    if (packed.size) {
      const b = pack(n, [...packed], up + s.gapY);
      parts.push({ key: b.key, mid: b.x + b.w / 2 });
    }
    const x = parts.length ? (parts[0].mid + (parts.at(-1) as (typeof parts)[number]).mid) / 2 - s.w / 2 : next;
    if (!parts.length) next += s.w + s.gapX;
    const p = { node: n, x, y: up };
    nodes.push(p);
    for (const k of parts) links.push({ key: k.key, x1: k.mid, to: p });
    return p;
  };
  const p = place(root, 0);
  const top = Math.max(...nodes.map((n) => n.y), ...blocks.map((b) => b.y));
  for (const n of nodes) n.y = top - n.y;
  for (const b of blocks) b.y = top - b.y;
  const edges = links.map((l) => ({ key: l.key, x1: l.x1, x2: l.to.x + s.w / 2, y: l.to.y }));
  return { root: p, nodes, blocks, edges, width: Math.max(s.w, next - s.gapX), height: top + s.h };
}

/** "×2", "×2.1": a box's quantity per finished unit. */
export const qtyText = (q: number) => `×${Number(q.toFixed(2))}`;
